import { useEffect } from 'react';
import { onLibraryWrite } from '../lib/library';
import { folderSyncRunning, getFolderSyncState, runFolderSync } from '../lib/folderSync';
import { useStore } from '../store/useStore';

/** How often to look at the folder for changes made outside markbird. */
const POLL_MS = 10_000;
/**
 * The same, for a repository. Reading a folder is free; asking GitHub is a
 * request against an hourly budget, and nobody edits a repo from elsewhere
 * ten times a minute. A change made here is still pushed within seconds.
 */
const GITHUB_POLL_MS = 120_000;
/** Quiet time after a change here before writing it out. */
const WRITE_DEBOUNCE_MS = 2_000;

/**
 * Runs folder sync while it is on: at start, when the window regains focus, every
 * few seconds while visible, and shortly after a document changes here.
 *
 * It waits while the open document has unsaved typing. Applying a change from
 * disk then would reload the editor over keystrokes that are not stored yet.
 */
export function useFolderSync(): void {
  useEffect(() => {
    let cancelled = false;
    let debounce: number | null = null;
    let lastPoll = 0;

    /** `local` = something changed here, so do not wait for the poll interval. */
    const tick = async (reason: 'poll' | 'local' = 'poll') => {
      if (cancelled || document.visibilityState !== 'visible') return;
      if (useStore.getState().saveState === 'saving') return;
      const state = await getFolderSyncState();
      if (!state.enabled) return;
      if (
        reason === 'poll' &&
        state.target === 'github' &&
        Date.now() - lastPoll < GITHUB_POLL_MS
      ) {
        return;
      }
      lastPoll = Date.now();
      await runFolderSync(false);
    };

    const unsubscribe = onLibraryWrite(() => {
      if (folderSyncRunning()) return;
      if (debounce !== null) window.clearTimeout(debounce);
      debounce = window.setTimeout(() => void tick('local'), WRITE_DEBOUNCE_MS);
    });
    const onFocus = () => void tick();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    const interval = window.setInterval(() => void tick(), POLL_MS);
    void tick();

    return () => {
      cancelled = true;
      unsubscribe();
      if (debounce !== null) window.clearTimeout(debounce);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      window.clearInterval(interval);
    };
  }, []);
}
